import React from 'react';
import { useWatch } from 'react-hook-form';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { MdOutlineCircle } from 'react-icons/md';
import styled from 'styled-components';
import { Flex } from '../../components/ui/Flex';
import { FormControl, FormField, FormItem, FormMessage } from '../../components/ui/Form';
import { Select, SelectItem } from '../../components/ui/Select';
import { Palette } from '../../components/utils';
import {
  DOC_ACCESS_OPTIONS,
  DOC_SCOPE,
  DOCUMENT_ROOT_FOLDER_VALUE,
  getDocScopeOptions,
  normalizeFolderSelectValue
} from './constants';
import DocumentThumbnailUpload from './DocumentThumbnailUpload';
import DocumentUploadedFileRow from './DocumentUploadedFileRow';
import {
  DocumentOptionCard,
  DocumentOptionDescription,
  DocumentOptionsList,
  DocumentOptionTitle,
  DocumentSection,
  DocumentSectionHeading
} from './documentWizardStyles';

const FolderHint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.125rem;
  color: ${Palette.BLACK_50};
`;

const BuildingSelectWrap = styled.div`
  width: 100%;
  margin-top: 0.75rem;

  .ant-select:not(.ant-select-open) .ant-select-selector {
    border: 1px solid ${Palette.STROKE_GREY} !important;
  }
`;

function OptionCards({ value, options, onChange, t, renderExtra, disabled = false }) {
  return (
    <DocumentOptionsList>
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <DocumentOptionCard
            key={option.value}
            $selected={selected}
            $disabled={disabled}
            onClick={() => {
              if (!disabled) {
                onChange(option.value);
              }
            }}
            role="radio"
            aria-checked={selected}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(event) => {
              if (disabled) {
                return;
              }
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onChange(option.value);
              }
            }}>
            {selected ? (
              <IoCheckmarkCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
            ) : (
              <MdOutlineCircle size={22} color={Palette.HAZY_BLUE} style={{ flexShrink: 0 }} />
            )}
            <Flex direction="column" alignItems="flex-start" gap="0.25rem" style={{ flex: 1 }}>
              <DocumentOptionTitle>{t(option.label, { defaultValue: option.label })}</DocumentOptionTitle>
              <DocumentOptionDescription>
                {t(option.description, { defaultValue: option.description })}
              </DocumentOptionDescription>
              {selected && renderExtra ? renderExtra(option) : null}
            </Flex>
          </DocumentOptionCard>
        );
      })}
    </DocumentOptionsList>
  );
}

function AddDocumentVisibilityStep({
  t,
  form,
  file,
  fileSizeBytes,
  onClearFile,
  existingFileName,
  isUploading = false,
  uploadProgress = null,
  uploadStalled = false,
  showScopeOptions = false,
  canChooseOrgWide = false,
  buildings = [],
  showFolderSelect = false,
  folders = [],
  foldersLoading = false,
  folderHint = '',
  showThumbnail = false,
  existingThumbnailUrl,
  thumbnailRemoved = false,
  onRemoveThumbnail,
  onRestoreThumbnail,
  thumbnailRemountKey
}) {
  const scope = useWatch({ control: form.control, name: 'scope' });
  const scopeOptions = getDocScopeOptions(showScopeOptions, canChooseOrgWide);
  const showFileSection = Boolean(file || existingFileName);
  const formLocked = isUploading;

  return (
    <Flex direction="column" gap="24px">
      {showFileSection ? (
        <DocumentSection>
          <DocumentSectionHeading>{t('File', { defaultValue: 'File' })}</DocumentSectionHeading>
          <DocumentUploadedFileRow
            t={t}
            file={file}
            fileName={existingFileName}
            fileSizeBytes={fileSizeBytes}
            onClearFile={onClearFile}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            uploadStalled={uploadStalled}
          />
        </DocumentSection>
      ) : null}

      {showThumbnail ? (
        <DocumentThumbnailUpload
          form={form}
          existingThumbnailUrl={existingThumbnailUrl}
          thumbnailRemoved={thumbnailRemoved}
          remountKey={thumbnailRemountKey}
          onRemoveExisting={onRemoveThumbnail}
          onRestoreExisting={onRestoreThumbnail}
          clearDisabled={formLocked}
        />
      ) : null}

      {showScopeOptions ? (
        <FormField
          control={form.control}
          name="scope"
          render={({ field }) => (
            <FormItem alt>
              <DocumentSection>
                <DocumentSectionHeading>
                  {t('Applies to', { defaultValue: 'Applies to' })}
                </DocumentSectionHeading>
                <FormControl>
                  <OptionCards
                    value={field.value}
                    options={scopeOptions}
                    onChange={field.onChange}
                    t={t}
                    disabled={formLocked}
                    renderExtra={(option) => {
                      if (option.value !== DOC_SCOPE.BUILDING || scope !== DOC_SCOPE.BUILDING) {
                        return null;
                      }

                      return (
                        <BuildingSelectWrap
                          onClick={(event) => event.stopPropagation()}
                          onKeyDown={(event) => event.stopPropagation()}>
                          <FormField
                            control={form.control}
                            name="buildingId"
                            render={({ field: buildingField }) => (
                              <FormItem alt style={{ width: '100%', marginBottom: 0 }}>
                                <FormControl>
                                  <Select
                                    id="document_building_id"
                                    placeholder={t('Select building', {
                                      defaultValue: 'Select building'
                                    })}
                                    value={buildingField.value || undefined}
                                    onChange={buildingField.onChange}
                                    onBlur={buildingField.onBlur}
                                    disabled={formLocked}
                                    height="48px">
                                    {buildings.length > 0 ? (
                                      buildings.map((building) => (
                                        <SelectItem key={building.id} value={building.id}>
                                          {building.name}
                                        </SelectItem>
                                      ))
                                    ) : (
                                      <SelectItem value={-1} disabled>
                                        {t('No Buildings Available')}
                                      </SelectItem>
                                    )}
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </BuildingSelectWrap>
                      );
                    }}
                  />
                </FormControl>
                <FormMessage />
              </DocumentSection>
            </FormItem>
          )}
        />
      ) : null}

      <FormField
        control={form.control}
        name="access"
        render={({ field }) => (
          <FormItem alt>
            <DocumentSection>
              <DocumentSectionHeading>
                {t('Visible to', { defaultValue: 'Visible to' })}
              </DocumentSectionHeading>
              <FormControl>
                <OptionCards
                  value={field.value}
                  options={DOC_ACCESS_OPTIONS}
                  onChange={field.onChange}
                  t={t}
                  disabled={formLocked}
                />
              </FormControl>
              <FormMessage />
            </DocumentSection>
          </FormItem>
        )}
      />

      {showFolderSelect ? (
        <FormField
          control={form.control}
          name="folderId"
          render={({ field }) => (
            <FormItem alt>
              <DocumentSection>
                <DocumentSectionHeading>
                  {t('Folder', { defaultValue: 'Folder' })}
                </DocumentSectionHeading>
                <FormControl>
                  <Select
                    id="document_folder_id"
                    placeholder={t('Select folder', {
                      defaultValue: 'Select folder'
                    })}
                    value={normalizeFolderSelectValue(field.value)}
                    onChange={(value) => field.onChange(normalizeFolderSelectValue(value))}
                    onBlur={field.onBlur}
                    disabled={formLocked || foldersLoading}
                    height="48px"
                    loading={foldersLoading}>
                    <SelectItem value={DOCUMENT_ROOT_FOLDER_VALUE}>
                      {t('No folder', { defaultValue: 'No folder' })}
                    </SelectItem>
                    {folders.map((folder) => (
                      <SelectItem key={folder.id} value={folder.id}>
                        {folder.shared
                          ? `${folder.name} (${t('Shared', { defaultValue: 'Shared' })})`
                          : folder.name}
                      </SelectItem>
                    ))}
                  </Select>
                </FormControl>
                {folderHint ? <FolderHint>{folderHint}</FolderHint> : null}
                <FormMessage />
              </DocumentSection>
            </FormItem>
          )}
        />
      ) : null}
    </Flex>
  );
}

export default AddDocumentVisibilityStep;
